import apiClient, { refreshAccessToken as refreshManagedAccessToken } from "./apiClient";
import axios from "./axiosConfig";
import axiosLib from "axios";
import API_URL from "./apiConfig";
import { convertToISO } from "../utils/dateValidation";
import tokenManager from "./tokenManager";
import Cookies from "js-cookie";

const API_KEY = process.env.REACT_APP_AWS_API_KEY;

const authAxios = axiosLib.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "x-api-key": API_KEY,
  },
});

authAxios.interceptors.request.use(
  (config) => {
    config.headers = config.headers || {};
    config.headers["x-api-key"] = API_KEY;

    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    return config;
  },
  (error) => Promise.reject(error)
);

const keepAllowedFields = (source, allowedFields) => {
  const data = {};

  allowedFields.forEach((field) => {
    const value = source?.[field];
    if (value !== undefined && value !== null) {
      data[field] = value;
    }
  });

  return data;
};

const isBlankValue = (value) => typeof value === "string" && value.trim() === "";

const normalizeMultipartValue = (field, value, options = {}) => {
  if (value === undefined || value === null) return undefined;
  if (isBlankValue(value) && !(field === "tagId" && options.allowEmptyTagId)) return undefined;

  if (field === "motherParity") {
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? undefined : numericValue;
  }

  if (["birthday", "sterilizationDate", "receivedDate"].includes(field)) {
    if (typeof value !== "string") return value;
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      return convertToISO(value) || undefined;
    }
  }

  if (typeof value === "boolean") return String(value);

  const isFile = typeof File !== "undefined" && value instanceof File;
  if (Array.isArray(value) || (typeof value === "object" && !isFile)) {
    return JSON.stringify(value);
  }

  return value;
};

const appendAllowedFields = (formData, source, allowedFields, options = {}) => {
  allowedFields.forEach((field) => {
    const value = normalizeMultipartValue(field, source?.[field], options);
    if (value !== undefined) {
      formData.append(field, value);
    }
  });
};

const extractPayload = (response) => response.data?.data ?? response.data;

const extractRecords = (response) => {
  const payload = extractPayload(response);

  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.records)) return payload.records;
  if (Array.isArray(response.data?.data)) return response.data.data;
  if (Array.isArray(response.data?.items)) return response.data.items;
  if (Array.isArray(response.data?.records)) return response.data.records;

  return [];
};

const normalizeIdFields = (item) => {
  if (!item || typeof item !== "object" || Array.isArray(item)) return item;

  const id = item.id || item._id;
  if (!id) return item;

  return {
    ...item,
    id,
    _id: item._id || id,
  };
};

const normalizeFormResponse = (response) => {
  const payload = normalizeIdFields(extractPayload(response));
  response.data = {
    ...response.data,
    data: payload,
    form: payload,
  };

  return response;
};

const normalizeListResponse = (response, legacyKey) => {
  const payload = extractPayload(response);
  const records = extractRecords(response).map(normalizeIdFields);

  response.data = {
    ...response.data,
    data: records,
    form: {
      ...(payload && typeof payload === "object" && !Array.isArray(payload) ? payload : {}),
      [legacyKey]: records,
    },
    pagination: payload?.pagination || response.data?.pagination,
  };

  return response;
};

const normalizePetsResponse = (response) => {
  const payload = extractPayload(response);
  const records = extractRecords(response).map(normalizeIdFields);

  response.data = {
    ...response.data,
    data: records,
    form: records,
    pagination: payload?.pagination || response.data?.pagination,
  };

  return response;
};

const NOTIFICATION_TYPE_TO_DDD = {
  nearby_pet_lost: "nearby_pet_lost",
  deworming_reminder: "deworming_reminder",
};

const NOTIFICATION_TYPE_FROM_DDD = {
  nearby_pet_lost: "nearby_pet_lost",
  deworming_reminder: "deworming_reminder",
};

const toDddNotificationType = (type) => NOTIFICATION_TYPE_TO_DDD[type] || type;

const fromDddNotificationType = (type) => NOTIFICATION_TYPE_FROM_DDD[type] || type;

const normalizeNotification = (notification) => {
  if (!notification || typeof notification !== "object") return notification;

  return {
    ...notification,
    type: fromDddNotificationType(notification.type),
  };
};

const normalizeNotificationResponse = (response) => {
  const payload = extractPayload(response);
  const notification = normalizeNotification(payload?.notification || payload);

  response.data = {
    ...response.data,
    data: notification,
    notification,
  };

  return response;
};

const removedEndpoint = (message) => {
  throw new Error(message);
};

const USER_PROFILE_FIELDS = [
  "firstName",
  "lastName",
  "birthday",
  "email",
  "district",
  "image",
  "phoneNumber",
];

const PET_PROFILE_FIELDS = [
  "name",
  "animal",
  "sex",
  "birthday",
  "breed",
  "weight",
  "sterilization",
  "sterilizationDate",
  "adoptionStatus",
  "bloodType",
  "features",
  "info",
  "status",
  "owner",
  "ownerContact1",
  "ownerContact2",
  "contact1Show",
  "contact2Show",
  "receivedDate",
  "location",
  "position",
  "tagId",
  "isRegistered",
  "breedimage",
];

const PET_DETAIL_FIELDS = [
  "chipId",
  "placeOfBirth",
  "motherName",
  "motherBreed",
  "motherDOB",
  "motherChip",
  "motherPlaceOfBirth",
  "motherParity",
  "fatherName",
  "fatherBreed",
  "fatherDOB",
  "fatherChip",
  "fatherPlaceOfBirth",
];

const FULFILLMENT_SUPPLIER_FIELDS = [
  "contact",
  "petName",
  "shortUrl",
  "masterEmail",
  "location",
  "petHuman",
  "pendingStatus",
  "qrUrl",
  "petUrl",
  "petContact",
];

const FULFILLMENT_TAG_FIELDS = [
  "contact",
  "verifyDate",
  "petName",
  "shortUrl",
  "masterEmail",
  "orderId",
  "location",
  "petHuman",
  "staffVerification",
];

const LOST_PET_FIELDS = [
  "petId",
  "name",
  "sex",
  "animal",
  "lostDate",
  "lostLocation",
  "lostDistrict",
  "birthday",
  "weight",
  "sterilization",
  "breed",
  "description",
  "remarks",
  "status",
  "owner",
  "ownerContact1",
];

const FOUND_PET_FIELDS = [
  "animal",
  "foundDate",
  "foundLocation",
  "foundDistrict",
  "breed",
  "description",
  "remarks",
  "status",
  "owner",
  "ownerContact1",
];

const appendImageFiles = (formData, files) => {
  if (!files) return;

  files.forEach((file) => {
    formData.append("files", file);
  });
};

export const register = async (userData) => {
  const data = keepAllowedFields(userData, [
    "firstName",
    "lastName",
    "email",
    "phoneNumber",
    "subscribe",
    "promotion",
    "district",
    "image",
    "birthday",
    "gender",
  ]);

  return authAxios.post("/auth/registrations/user", data, { withCredentials: true });
};

export const registerbyPhoneNumber = async (userData) => register(userData);

export const registerbyEmail = async (userData) => register(userData);

export const generateSmsCode = async (phoneNumber) => {
  return authAxios.post("/auth/challenges", { phoneNumber }, { withCredentials: false });
};

export const VerifySmsCode = async (phoneNumber, code) => {
  return authAxios.post("/auth/challenges/verify", { phoneNumber, code }, { withCredentials: true });
};

export const generateEmailCode = async (email) => {
  return authAxios.post("/auth/challenges", { email }, { withCredentials: false });
};

export const VerifyEmailCode = async (email, resetCode) => {
  return authAxios.post("/auth/challenges/verify", { email, code: resetCode }, { withCredentials: true });
};

export const getUserProfile = async () => {
  const response = await apiClient.get("/user/me", { withCredentials: false });
  const user = response.data?.data || response.data?.user || response.data;

  response.data = {
    ...response.data,
    data: user,
    user,
  };

  return response;
};

export const getPets = async (userId, page = 1) => {
  const response = await apiClient.get(`/pet/profile/me?page=${page}`, { withCredentials: false });
  return normalizePetsResponse(response);
};

export const refreshAccessToken = async () => refreshManagedAccessToken();

export const updateUserProfile = async (userData) => {
  const response = await axios.patch("/user/me", keepAllowedFields(userData, USER_PROFILE_FIELDS));
  return normalizeFormResponse(response);
};

//admin
export const getTagInfoforSupplier = async (orderId) => {
  const response = await axios.get(`/commerce/fulfillment/suppliers/${orderId}`);
  return normalizeFormResponse(response);
};

//admin
export const getOrderInfo = async (tempId) => {
  const response = await axios.get(`/commerce/orders/${tempId}`);
  return normalizeFormResponse(response);
};


export const getTagInfoforWhatsAppLink = async (_id) => {
  const response = await axios.get(`/commerce/fulfillment/share-links/whatsapp/${_id}`);
  return normalizeFormResponse(response);
};

//admin
export const updateTagInfoforSupplier = async (tagId, tagData) => {
  const response = await axios.patch(
    `/commerce/fulfillment/suppliers/${tagId}`,
    keepAllowedFields(tagData, FULFILLMENT_SUPPLIER_FIELDS)
  );

  return normalizeFormResponse(response);
};

export const getTagInfo = async (tagId) => {
  const response = await axios.get(`/commerce/fulfillment/tags/${tagId}`);
  return normalizeFormResponse(response);
};

//admin
export const updateTagInfo = async (tagId, tagData) => {
  const response = await axios.patch(
    `/commerce/fulfillment/tags/${tagId}`,
    keepAllowedFields(tagData, FULFILLMENT_TAG_FIELDS)
  );

  return normalizeFormResponse(response);
};

export const createPet = async (userId, petData) => {
  const formData = new FormData();
  appendImageFiles(formData, petData.imageFiles);
  appendAllowedFields(formData, petData, PET_PROFILE_FIELDS);

  const response = await axios.post("/pet/profile", formData);
  return normalizeFormResponse(response);
};

export const deletePet = async (petId) => {
  return axios.delete(`/pet/profile/${petId}`);
};

export const getPetBasicInfo = async (petId) => {
  const response = await axios.get(`/pet/profile/${petId}?view=basic`);
  return normalizeFormResponse(response);
};

export const getPetInfobyTagId = async (tagId) => {
  const response = await axios.get(`/pet/profile/by-tag/${tagId}`);
  return normalizeFormResponse(response);
};

export const updatePetBasicInfo = async (petId, petData) => {
  const formData = new FormData();
  appendAllowedFields(formData, petData, PET_PROFILE_FIELDS);

  const response = await axios.patch(`/pet/profile/${petId}`, formData);
  return normalizeFormResponse(response);
};

export const updatePetImage = async (basicInfo, petId, removedImageIndices = []) => {
  const formData = new FormData();
  appendImageFiles(formData, basicInfo.imageFiles);
  appendAllowedFields(formData, basicInfo, PET_PROFILE_FIELDS, { allowEmptyTagId: true });
  if (removedImageIndices.length > 0) {
    formData.append("removedIndices", JSON.stringify(removedImageIndices));
  }

  const response = await axios.patch(`/pet/profile/${petId}`, formData);
  return normalizeFormResponse(response);
};

export const getPetDetail = async (petId) => {
  const response = await axios.get(`/pet/profile/${petId}?view=detail`);
  return normalizeFormResponse(response);
};

export const updatePetDetail = async (petId, petData) => {
  const formData = new FormData();
  appendAllowedFields(formData, petData, PET_DETAIL_FIELDS);

  const response = await axios.patch(`/pet/profile/${petId}`, formData);
  return normalizeFormResponse(response);
};

export const createTransferRecord = async (petId, transferData) => {
  return axios.post(`/pet/transfer/${petId}`, transferData);
};

export const updateTransferRecord = async (petId, transferId, transferData) => {
  return axios.patch(`/pet/transfer/${petId}/${transferId}`, transferData);
};

//admin
export const updateNGOtransfer = async (petId, transferData) => {
  return axios.post(`/pet/transfer/${petId}/ngo-reassignment`, transferData);
};

export const deleteTransferRecord = async (petId, transferId) => {
  return axios.delete(`/pet/transfer/${petId}/${transferId}`);
};

export const getPetMedical = async (petId) => {
  const response = await axios.get(`/pet/medical/${petId}/general`);
  return normalizeListResponse(response, "medical");
};

export const updatePetMedical = async (petId, petData) => {
  return axios.post(`/pet/medical/${petId}/general`, petData);
};

export const createMedicalRecord = async (petId, medicalData) => {
  return axios.post(`/pet/medical/${petId}/general`, medicalData);
};

export const updateMedicalRecord = async (petId, medicalID, medicalData) => {
  return axios.patch(`/pet/medical/${petId}/general/${medicalID}`, medicalData);
};

export const deleteMedicalRecord = async (petId, medicalID) => {
  return axios.delete(`/pet/medical/${petId}/general/${medicalID}`);
};

export const getPetBloodTest = async (petId) => {
  const response = await axios.get(`/pet/medical/${petId}/blood-test`);
  return normalizeListResponse(response, "blood_test");
};

export const createBloodTestRecord = async (petId, bloodTestData) => {
  return axios.post(`/pet/medical/${petId}/blood-test`, bloodTestData);
};

export const updateBloodTestRecord = async (petId, bloodTestId, bloodTestData) => {
  return axios.patch(`/pet/medical/${petId}/blood-test/${bloodTestId}`, bloodTestData);
};

export const deleteBloodTestRecord = async (petId, bloodTestId) => {
  return axios.delete(`/pet/medical/${petId}/blood-test/${bloodTestId}`);
};

export const getPetMedication = async (petId) => {
  const response = await axios.get(`/pet/medical/${petId}/medication`);
  return normalizeListResponse(response, "medication");
};

export const updatePetMedication = async (petId, petData) => {
  return axios.post(`/pet/medical/${petId}/medication`, petData);
};

export const createMedicationRecord = async (petId, medicationData) => {
  return axios.post(`/pet/medical/${petId}/medication`, medicationData);
};

export const updateMedicationRecord = async (petId, medicationID, medicationData) => {
  return axios.patch(`/pet/medical/${petId}/medication/${medicationID}`, medicationData);
};

export const deleteMedicationRecord = async (petId, medicationID) => {
  return axios.delete(`/pet/medical/${petId}/medication/${medicationID}`);
};

export const getPetDeworming = async (petId) => {
  const response = await axios.get(`/pet/medical/${petId}/deworming`);
  return normalizeListResponse(response, "deworm");
};

export const updatePetDeworming = async (petId, petData) => {
  return axios.post(`/pet/medical/${petId}/deworming`, petData);
};

export const createDewormingRecord = async (petId, dewormingData) => {
  return axios.post(`/pet/medical/${petId}/deworming`, dewormingData);
};

export const updateDewormingRecord = async (petId, dewormID, dewormingData) => {
  return axios.patch(`/pet/medical/${petId}/deworming/${dewormID}`, dewormingData);
};

export const deleteDewormingRecord = async (petId, dewormID) => {
  return axios.delete(`/pet/medical/${petId}/deworming/${dewormID}`);
};

export const updatePetVaccination = async (petId, petData) => {
  return axios.post(`/pet/medical/${petId}/vaccination`, petData);
};

export const createVaccinationRecord = async (petId, vaccinationData) => {
  return axios.post(`/pet/medical/${petId}/vaccination`, vaccinationData);
};

export const updateVaccinationRecord = async (petId, vaccineID, vaccinationData) => {
  return axios.patch(`/pet/medical/${petId}/vaccination/${vaccineID}`, vaccinationData);
};

export const deleteVaccinationRecord = async (petId, vaccineID) => {
  return axios.delete(`/pet/medical/${petId}/vaccination/${vaccineID}`);
};

const normalizeBreedResponse = (response) => {
  const payload = extractPayload(response);
  const breeds =
    (Array.isArray(payload) && payload) ||
    (Array.isArray(payload?.breeds) && payload.breeds) ||
    (Array.isArray(payload?.result) && payload.result) ||
    (Array.isArray(payload?.items) && payload.items) ||
    (Array.isArray(payload?.records) && payload.records) ||
    [];

  response.data = {
    ...response.data,
    data: breeds,
    result: breeds,
  };

  return response;
};

export const getDogBreeds = async (lang = "en") => {
  const response = await axios.get(`/pet/reference/breed/dog?lang=${encodeURIComponent(lang)}`);
  return normalizeBreedResponse(response);
};

export const getCatBreeds = async (lang = "en") => {
  const response = await axios.get(`/pet/reference/breed/cat?lang=${encodeURIComponent(lang)}`);
  return normalizeBreedResponse(response);
};

export const getPetVaccination = async (petId) => {
  const response = await axios.get(`/pet/medical/${petId}/vaccination`);
  return normalizeListResponse(response, "vaccineRecords");
};

export const getPetBehaviorSummary = async (petId, from, to) => {
  return axios.get(`/pet/behavior/summary?petId=${petId}&from=${from}&to=${to}`);
};

export const getPetBehaviorTimeline = async (petId, from, to, bucket = "1d") => {
  return axios.get(`/pet/behavior/timeline?petId=${petId}&from=${from}&to=${to}&bucket=${bucket}`);
};

export const verifyShopCode = async (shopCode, file) => {
  const normalizedShopCode = typeof shopCode === "string" ? shopCode.trim().toUpperCase() : "";

  if (file) {
    const formData = new FormData();
    if (normalizedShopCode) {
      formData.append("shopCode", normalizedShopCode);
    }
    formData.append("file", file);

    return axios.post("/commerce/storefront/shop-code-verifications", formData);
  }

  return axios.post("/commerce/storefront/shop-code-verifications", {
    shopCode: normalizedShopCode,
  });
};

export const uploadBookingDocument = async (bookingId, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return axios.post(`/rooms/bookings/${bookingId}/upload`, formData);
};

export const getReviewDocument = async (bookingId) => {
  return apiClient.get(`/rooms/bookings/${bookingId}/document`);
};

export const deleteReviewDocument = async (bookingId) => {
  return axios.delete(`/rooms/bookings/${bookingId}/document`);
};

export const getPtagProduct = async (productId) => {
  const response = await axios.get(`/commerce/catalog/ptag-products/${productId}`);
  return normalizeFormResponse(response);
};

export const createPetLostRecord = async (userId, petData) => {
  const formData = new FormData();
  appendImageFiles(formData, petData.imageFiles);
  appendAllowedFields(formData, petData, LOST_PET_FIELDS);

  return axios.post("/pet/recovery/lost", formData);
};

export const deletePetLostRecord = async (petLostID) => {
  return axios.delete(`/pet/recovery/lost/${petLostID}`);
};

export const createPetFoundRecord = async (petData) => {
  const formData = new FormData();
  appendImageFiles(formData, petData.imageFiles);
  appendAllowedFields(formData, petData, FOUND_PET_FIELDS);

  return axios.post("/pet/recovery/found", formData);
};

export const getListofLostPets = async (page = 1, limit = 30, filters = {}) => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  // Add optional filters
  if (filters.search) params.append('search', filters.search);
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
  if (filters.lastDays !== 'all') {
    params.append('lastDays', (filters.lastDays || 60).toString());
  }

  const response = await axios.get(`/pet/recovery/lost?${params.toString()}`);
  return response;
};

export const getListofFoundPets = async (page = 1, limit = 30, filters = {}) => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  // Add optional filters
  if (filters.search) params.append('search', filters.search);
  if (filters.sortBy) params.append('sortBy', filters.sortBy);
  if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
  if (filters.lastDays !== 'all') {
    params.append('lastDays', (filters.lastDays || 60).toString());
  }

  const response = await axios.get(`/pet/recovery/found?${params.toString()}`);
  return response;
};

export const sendPtagDetectionEmail = async (name, petTagNumber, currentDateTime, googleMapsLink, email) => {
  return axios.post("/commerce/commands/ptag-detection-email", {
    name,
    tagId: petTagNumber,
    dateTime: currentDateTime,
    locationURL: googleMapsLink,
    email,
  });
};

const TAG_UPDATE_API_BASE = process.env.REACT_APP_AWS_PETAPI_DEV;

export const UploadtagUpdateImages = async ({ orderId, qrImageFile, petImageFile }) => {
  const formData = new FormData();
  formData.append("orderId", orderId);
  if (qrImageFile) formData.append("qrImage", qrImageFile);
  if (petImageFile) formData.append("petImage", petImageFile);

  const token = tokenManager.getAuthToken() || Cookies.get("token");

  return authAxios.post(`${TAG_UPDATE_API_BASE}/ptag/admin/updateImages`, formData, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

export const getNotifications = async () => {
  const response = await axios.get("/notifications/me");
  const records = extractRecords(response).map(normalizeNotification);

  response.data = {
    ...response.data,
    notifications: records,
  };

  return response;
};

export const PostNotifications = async (data) => {
  const response = await axios.post("/notifications/dispatch", {
    ...data,
    type: toDddNotificationType(data.type),
  });

  return normalizeNotificationResponse(response);
};

export const ArchiveNotifications = async (userId, notificationId) => {
  return axios.patch(`/notifications/me/${notificationId}`);
};

export const getAllOrders = async (page = 1, limit = 30) => {
  const response = await axios.get(
    `/commerce/orders/operations?page=${encodeURIComponent(page)}&limit=${encodeURIComponent(limit)}`
  );
  const records = extractRecords(response);

  response.data = {
    ...response.data,
    allOrders: records,
  };

  return response;
};

//admin
export const cancelOrder = async (orderVerificationId) => {
  return axios.delete(`/commerce/fulfillment/${orderVerificationId}`);
};
